import React, { memo, useMemo } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Collapse,
  TextField,
  IconButton,
  Link as MuiLink,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PickupIcon from "../../../img/pickup-icon.svg";
import DeliveryIcon from "../../../img/delivery-icon.svg";
import ReturnsIcon from "../../../img/online-returns-icon.svg";
import type { CouponCode } from "../../models/cart/types";
import moment from "moment";
import { LoadingStatusEnum } from "@/enums/loadingStatusEnum";

const formatMoney = (n?: number) =>
  typeof n === "number"
    ? `$${n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
    : "-";

const formatPhoneDash = (input?: string) => {
  if (!input) return "";
  const digits = String(input).replace(/\D/g, "");
  if (digits.length !== 10) return input; // fallback if not 10 digits
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export type CartCheckoutSummaryProps = {
  itemsCount: number;
  subtotal: number;
  city?: string;
  warehouseEmail?: string;
  holidays: string[];
  contactPhoneNumber?: string;
  disableCheckoutButton: boolean;
  hideCheckoutButton: boolean;

  fulfillment: "pickup" | "delivery";
  onChangeFulfillment: (m: "pickup" | "delivery") => void;

  onCheckout: () => void;

  // promo
  discount: number;
  showPromo: boolean;
  onTogglePromo: () => void;
  promoCode: string;
  onChangePromo: (s: string) => void;
  onApplyPromo: () => void;
  loadingStatus: LoadingStatusEnum;
  setLoadingStatus: (status: LoadingStatusEnum) => void;

  appliedCodes: CouponCode[];
  onRemoveCode: (couponCode: string) => void;
  couponMessages: string[];
  couponApplied: boolean;

  showTerms: boolean;
  onOpenTerms: () => void;

  onOpenSidebar: (open: boolean) => void;
  cartHasEagleView: boolean;
  eagleViewAddressAvailable?: boolean;
};

const CartCheckoutSummaryComponent: React.FC<CartCheckoutSummaryProps> = ({
  itemsCount,
  subtotal,
  city,
  warehouseEmail,
  contactPhoneNumber,
  fulfillment,
  onChangeFulfillment,
  onCheckout,
  holidays,
  disableCheckoutButton,
  hideCheckoutButton,

  discount,
  showPromo,
  onTogglePromo,
  promoCode,
  onChangePromo,
  onApplyPromo,
  loadingStatus,
  setLoadingStatus,

  appliedCodes,
  onRemoveCode,
  couponMessages,
  couponApplied,

  showTerms,
  onOpenTerms,
  onOpenSidebar,
  cartHasEagleView,
  eagleViewAddressAvailable,
}) => {
  const promoHasError = promoCode.length > 0 && couponMessages.length > 0 && !couponApplied;
  const hoursLimit = 14;

  // Pickup
  const getDateSkipHoliday = (date: Date, holidays: string[]): Date => {
    const formatDate = (d: Date) => moment(d).format('YYYY-MM-DD');
    const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6;
    const isHoliday = (d: Date) => holidays.includes(formatDate(d));

    const skip = (d: Date): Date => {
      const dateStr = formatDate(d);

      if (!isWeekend(d)) {
        if (isHoliday(d)) {
          const next = new Date(d);
          next.setDate(d.getDate() + 1);
          return skip(next);
        } else {
          return d;
        }
      } else {
        const next = new Date(d);
        next.setDate(d.getDate() + 1);
        return skip(next);
      }
    };

    return skip(date);
  };

  const getMinPickupDate = (): string => {
    const now = new Date();
    const localHour = now.getHours();

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    let minPickUpDate = localHour >= hoursLimit ? new Date(tomorrow) : new Date(today);

    while (minPickUpDate.getDay() === 0 || minPickUpDate.getDay() === 6) {
      minPickUpDate.setDate(minPickUpDate.getDate() + 1);
    }

    minPickUpDate = getDateSkipHoliday(minPickUpDate, holidays);

    const label = minPickUpDate.toDateString() === today.toDateString()
      ? 'today'
      : minPickUpDate.toDateString() === tomorrow.toDateString()
        ? 'tomorrow'
        : moment(minPickUpDate).format('MM-DD-YYYY');

    return label;
  };

  const calculateMinDeliveryDate = (
    today: Date,
    hoursLimit: number,
    holidays: string[]
  ): Date => {
    const formatDate = (d: Date) => moment(d).format('YYYY-MM-DD');
    const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6;
    const isHoliday = (d: Date) => holidays.includes(formatDate(d));

    let minDeliveryDate = new Date(today);
    let needsExtraIteration = false;

    const currentHour = today.getHours();

    const isWeekendNow = isWeekend(minDeliveryDate);
    const isHolidayNow = isHoliday(minDeliveryDate);

    if (currentHour >= hoursLimit || isWeekendNow || isHolidayNow) {
      minDeliveryDate.setDate(minDeliveryDate.getDate() + 1);
      needsExtraIteration = true;
    } else {
      minDeliveryDate.setDate(minDeliveryDate.getDate() + 1);
    }

    while (true) {
      const isWeekendDay = isWeekend(minDeliveryDate);
      const isHolidayDay = isHoliday(minDeliveryDate);

      if (isWeekendDay || isHolidayDay) {
        minDeliveryDate.setDate(minDeliveryDate.getDate() + 1);
      } else if (needsExtraIteration) {
        minDeliveryDate.setDate(minDeliveryDate.getDate() + 1);
        needsExtraIteration = false;
      } else {
        break;
      }
    }

    return minDeliveryDate;
  };

  const storeLinePickup = useMemo(() => (
    <>
      at <span style={{ fontWeight: 700 }}>{city ? city : "-"}</span> store as soon as <br />
      <span className="min-pickup-date" style={{ fontWeight: 700, fontSize: 20, marginTop: 4 }}>
        {" "}{getMinPickupDate()}
      </span>
    </>
  ), [city]);

  // Delivery
  const { deliveryDateFormatted } = useMemo(() => {
    const today = new Date();
    const deliveryDate = calculateMinDeliveryDate(today, hoursLimit, holidays);
    const deliveryDateFormatted = moment(deliveryDate).format('MM-DD-YYYY');

    return { deliveryDate, deliveryDateFormatted };
  }, [hoursLimit, holidays]);

  const storeLineDelivery = useMemo(() => (
    <>
      to your jobsite as soon as <br />
      <div className="min-delivery-date" style={{ fontWeight: 700, fontSize: 20, marginTop: 6 }}>
        {" "}{deliveryDateFormatted}
      </div>
    </>
  ), []);

  return (
    <Box sx={{ mb: 10 }}>
      <Box
        className="order-summary-total"
        sx={{
          bgcolor: "#f6f6f6",
          borderRadius: 2,
          p: { xs: 2, md: 3 },
          fontFamily: "Montserrat, Verdana, sans-serif",
          color: "#767676",
          fontSize: 14,
          lineHeight: "17px",
        }}
      >
        {/* Checkout (top) */}
        {!hideCheckoutButton && <Box className="order-summary-checkout" sx={{ mb: 2 }}>
          <Button
            id="checkout"
            fullWidth
            className="blueButtonSolid"
            onClick={onCheckout}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              height: 40,
            }}
            disabled={disableCheckoutButton}
          >
            Checkout
          </Button>
        </Box>
        }

        {/* Your Order Subtotal */}
        <Box className="total-info order-sub-total-container" sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h5" className="sub-total-title" sx={{ fontWeight: 600 }}>
            Your Order Subtotal
          </Typography>
          <Divider className="break-line" sx={{ my: 1 }} />
          <Box className="order-subtotal" sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
            <Typography>{itemsCount === 1 ? "1 Item" : `${itemsCount} Items`}</Typography>
            <Typography id="cart-sub-total-with-discount" className="value-summary" sx={{ fontWeight: 600 }}>
              {formatMoney(subtotal)}
            </Typography>
          </Box>
          {couponApplied ?
            <Box>
              <Box sx={{ color: "#29b05f", display: "flex", justifyContent: "space-between", py: 1 }}>
                <Typography fontWeight="600" id="discount-code">Promo Savings [{appliedCodes[0].couponCode}]</Typography>
                <Typography fontWeight="600" id="discount-amount">-{formatMoney(discount)}</Typography>
              </Box>
              <Divider className="break-line" sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
                <Typography fontWeight="600" fontSize={20} id="discount-code">Subtotal</Typography>
                <Typography fontWeight="600" fontSize={20} id="discount-amount">{formatMoney(subtotal - discount)}</Typography>
              </Box>
            </Box>
            :
            <Divider className="break-line" sx={{ my: 1 }} />
          }
          <Typography color="#f00" fontStyle={"italic"} fontSize={10} fontWeight={400}>
            Applicable taxes or service charges are not reflected until your order is processed.<br />
            Prices are subject to change by branch.
          </Typography>
        </Box>

        {/* Delivery / Pickup section */}
        <Box className="delivery-pickup-section" sx={{ display: "flex", justifyContent: "space-evenly", my: "28px", gap: 1 }}>
          {/* Pick Up */}
          <input type="radio" id="pickup-id" name="PickUpInStore" checked={fulfillment === "pickup"} readOnly hidden />
          <Tooltip
            title={cartHasEagleView ? "Pickup is not available for EagleView orders" : ""}
            arrow
            disableHoverListener={!cartHasEagleView}
          >
            <Box
              component="label"
              htmlFor="pickup-id"
              className="delivery-pickup-box"
              onClick={cartHasEagleView ? undefined : () => onChangeFulfillment("pickup")}
              sx={{
                width: 178.53,
                height: 160,
                bgcolor: "#FFFFFF",
                border: fulfillment === "pickup" ? "2px solid #62B5E5" : "1px solid #A8A8A8",
                borderRadius: "3px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                p: 2,
                cursor: cartHasEagleView ? "not-allowed" : "pointer",
                alignItems: "center",
                position: "relative",
                opacity: cartHasEagleView ? 0.5 : 1,
                "&:before":
                  fulfillment === "pickup"
                    ? {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 24,
                      height: 24,
                      background: `url('/images/radio-button-checked.svg') center no-repeat`,
                    }
                    : {},
              }}
            >
              <img src={PickupIcon} alt="PickupIcon" />
              <span
                className="delivery-pickup-text"
                style={{ fontWeight: 500, fontSize: 12, color: "#000", marginTop: 15, textAlign: "center" }}
              >
                <span className="delivery-pickup-title" style={{ fontWeight: 700, fontSize: 20, display: "block", marginBottom: 8 }}>
                  Pickup
                </span>
                {storeLinePickup}
              </span>
            </Box>
          </Tooltip>

          {/* Delivery */}
          <input type="radio" id="delivery-id" name="PickUpInStore" checked={fulfillment === "delivery"} readOnly hidden />
          <Box
            component="label"
            htmlFor="delivery-id"
            className="delivery-pickup-box"
            onClick={() => onChangeFulfillment("delivery")}
            sx={{
              width: 178.53,
              height: 160,
              bgcolor: "#FFFFFF",
              border: fulfillment === "delivery" ? "2px solid #62B5E5" : "1px solid #A8A8A8",
              borderRadius: "3px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              p: 2,
              cursor: "pointer",
              alignItems: "center",
              position: "relative",
              "&:before":
                fulfillment === "delivery"
                  ? {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 24,
                    height: 24,
                    background: `url('/images/radio-button-checked.svg') center no-repeat`,
                  }
                  : {},
            }}
          >
            <img src={DeliveryIcon} alt="Delivery Icon" />
            <span
              className="delivery-pickup-text"
              style={{ fontWeight: 500, fontSize: 12, color: "#000", marginTop: 15, textAlign: "center" }}
            >
              <span className="delivery-pickup-title" style={{ fontWeight: 700, fontSize: 20, display: "block", marginBottom: 8 }}>
                Delivery
              </span>
              {storeLineDelivery}
            </span>
          </Box>
        </Box>

        {/* EagleView info note */}
        {cartHasEagleView && (
          <Box sx={{ bgcolor: "#e8f4fd", borderRadius: 1, p: 1.5, mb: 2 }}>
            <Typography variant="body2" sx={{ color: "#0076CE" }}>
              {eagleViewAddressAvailable
                ? "EagleView property address saved. Delivery is required for EagleView orders."
                : "An EagleView property address is required and will be entered at checkout."}
            </Typography>
          </Box>
        )}

        {/* Coupon / Promo section */}
        {!cartHasEagleView && <Box className="cupon-section" sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Typography
            className="cupon-title footer-action cupon-click"
            onClick={onTogglePromo}
            sx={{
              mb: "12px",
              fontWeight: 600,
              fontSize: 14,
              color: "#003B5C",
              cursor: "pointer",
            }}
          >
            Have a Promo Code?{" "}
            <Typography component="span" sx={{ textDecoration: "underline", fontWeight: 400 }}>
              Click Here.
            </Typography>
          </Typography>

          <Collapse in={showPromo} unmountOnExit>
            <Box id="cuponBox" sx={{ mb: "12px" }}>
              <Box id="apply-box" sx={{ display: couponApplied ? "none" : "flex", alignItems: { xs: "stretch", sm: "center" }, gap: 1 }}>
                <TextField
                  id="discountcouponcode"
                  value={promoCode}
                  onChange={(e) => {
                    onChangePromo(e.target.value)
                    setLoadingStatus(LoadingStatusEnum.IDLE);
                  }}
                  size="small"
                  sx={{
                    maxWidth: 286,
                    height: 40,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#FFFFFF",
                      borderRadius: "2px",
                      "&.Mui-focused fieldset": {
                        borderColor: promoHasError ? "#ed3b4b" : "transparent",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: promoHasError ? "#ed3b4b" : "transparent",
                      },
                      "& input": {
                        color: promoHasError ? "#ed3b4b" : "black",
                      },
                    },
                  }}
                />
                {loadingStatus === LoadingStatusEnum.LOADING ?
                  <div style={{ width: '100px' }}><LinearProgress /></div> :
                  <Button
                    id="applydiscountcouponcode"
                    onClick={onApplyPromo}
                    variant="outlined"
                    className="blueButtonOutline"
                    disabled={loadingStatus === LoadingStatusEnum.SUCCESS || promoCode.trim().length === 0}
                    sx={{
                      minHeight: 32,
                      height: "auto",
                      fontWeight: 600,
                      fontSize: 15,
                      lineHeight: "15px"
                    }}
                  > Apply Promo
                  </Button>
                }
              </Box>

              {/* Applied codes */}
              {discount > 0 && appliedCodes.map((d) => (
                <Box key={d.id} className="current-code" sx={{ mt: "10px", display: "flex", alignItems: "center", gap: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, lineHeight: "17.19px" }} className="applied-discount-code">
                    {d.couponCode}
                  </span>
                  <IconButton
                    size="small"
                    aria-label="remove promo"
                    onClick={() => onRemoveCode(d.couponCode)}
                    sx={{ width: 16, height: 16, border: "1.5px solid #696969", borderRadius: "10px", p: "2px" }}
                  >
                    <CloseIcon fontSize="small" sx={{ width: 14, height: 14 }} />
                  </IconButton>
                </Box>
              ))}

              {/* Applied message / error */}
              {couponMessages.map((m, idx) => (
                <Box key={idx} className={couponApplied ? "message-success" : "message-failure"} sx={{ fontSize: 13, mt: 0.5 }}>
                  <span style={{ color: couponApplied ? "#29b05f" : "#ed3b4b" }}>{m}</span>
                  {showTerms && (
                    <Typography
                      component="span"
                      onClick={onOpenTerms}
                      sx={{ ml: 1, textDecoration: "underline", cursor: "pointer", color: "#000" }}
                    >
                      *See terms
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Collapse>

          <Typography className="cupon-title footer-action" sx={{ fontWeight: 600, fontSize: 14, color: "#003B5C", cursor: "pointer" }} onClick={() => onOpenSidebar(true)}>
            Create Template or Estimate?
          </Typography>
        </Box>}

        {/* Checkout */}
        {!hideCheckoutButton && <Box className="order-summary-checkout" sx={{ mt: 2 }}>
          <Button
            id="checkout-bottom"
            className="blueButtonSolid"
            fullWidth
            disabled={disableCheckoutButton}
            onClick={onCheckout}
            sx={{
              textTransform: "none",
              fontWeight: 800,

            }}
          >
            Checkout
          </Button>
        </Box>
        }

        {/* Sticky checkout (mobile mimic) */}
        {/* <Box
            className="order-summary-checkout-sticky"
            sx={{
              display: { xs: "block", md: "none" },
              position: "sticky",
              bottom: -5,
              mt: 2,
              bgcolor: "#EAEAEA",
              p: 1,
              boxShadow: "0 0 8px 0 gray",
              zIndex: 5,
            }}
          >
            <Button
              id="checkout-sticky"
              fullWidth
              variant="contained"
              onClick={onCheckout}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                height: 40,
              }}
            >
              Checkout
            </Button>
          </Box> */}
      </Box>

      {/* Return policy + support */}
      <Box
        className="return-policy"
        sx={{
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          my: "20px",
          justifyContent: "center",
          maxWidth: 800,
        }}
      >
        <Box className="policy-icon" sx={{ mr: "10px" }}>
          <img src={ReturnsIcon} alt="Returns Icon" />
        </Box>
        <Box className="return-info">
          <span className="col-md-12" style={{ fontWeight: 600, fontSize: 18, lineHeight: "22px" }}>
            In-Store Returns
          </span>
          <br />
          <span className="read-our col-md-12" style={{ fontWeight: 400 }}>
            Read our{" "}
            <MuiLink
              href={process.env.REACT_APP_WEB_URL + "/return-privacy"}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "#0076CE", fontWeight: 500, display: "inline-flex", alignItems: "center" }}
            >
              Return Policy
            </MuiLink>
          </span>
        </Box>
      </Box>

      <Divider className="break-line" sx={{ my: 2, borderBottom: "1px solid #000 !important" }} />

      <Box className="support-info" sx={{ textAlign: "left", mb: "5px", p: 0 }}>
        <span style={{ fontWeight: 700 }}>Need Help?</span>
      </Box>
      <Box className="checkout-pickup-point col-md-12 col-sm-12" sx={{ pl: 0, textAlign: "left" }}>
        Read our{" "}
        <MuiLink
          href={process.env.REACT_APP_WEB_URL + "/faq"}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "#0076CE", fontWeight: 500, display: "inline-flex", alignItems: "center" }}
        >
          FAQ
        </MuiLink>
      </Box>
      <Box className="checkout-pickup-point col-md-12 col-sm-12" sx={{ pl: 0, textAlign: "left" }}>
        Call your local branch:{" "}
        <a href={`tel:${contactPhoneNumber}`} style={{ color: "#0076CE", fontWeight: 500 }}>
          {formatPhoneDash(contactPhoneNumber)}
        </a>
      </Box>
      <Box className="checkout-pickup-point col-md-12 col-sm-12" sx={{ pl: 0, textAlign: "left" }}>
        Email your local Branch:{" "}
        <a href={`mailto:${warehouseEmail}`} style={{ color: "#0076CE", fontWeight: 500 }}>
          {warehouseEmail}
        </a>
      </Box>
    </Box>
  );
};

export const CartCheckoutSummary = memo(CartCheckoutSummaryComponent);
export default CartCheckoutSummary;